import { ArrowDown, Zap } from "lucide-react";
import { motion } from "framer-motion";
import BookButton from "@/components/site/BookButton";
import { PRODUCT_IMAGE, TAGLINE } from "@/lib/site";

export default function Hero() {
    return (
        <section
            data-testid="hero-section"
            className="relative min-h-[100vh] flex items-center overflow-hidden electric-bg"
        >
            <div className="absolute inset-0 electric-grid" />
            <div
                aria-hidden
                className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[#1E90FF]/15 blur-[140px] animate-pulse-glow pointer-events-none"
            />
            <div
                aria-hidden
                className="absolute -bottom-40 right-0 w-[500px] h-[500px] rounded-full bg-[#00B7FF]/10 blur-[120px] pointer-events-none"
            />

            <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-32 pb-16 md:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="inline-flex items-center gap-3 px-3 py-1.5 border border-[#1E90FF]/40 bg-[#1E90FF]/10 backdrop-blur-sm"
                        >
                            <Zap size={14} className="text-[#00B7FF]" fill="currentColor" />
                            <span className="text-[10px] sm:text-xs uppercase tracking-[0.28em] font-bold text-white">
                                {TAGLINE}
                            </span>
                        </motion.div>

                        <motion.h1
                            data-testid="hero-headline"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            className="mt-8 font-display font-black text-white leading-[0.92] tracking-tighter text-5xl sm:text-6xl lg:text-7xl xl:text-[88px]"
                        >
                            Become More{" "}
                            <span className="text-[#00B7FF] text-glow-blue">
                                Attractive
                            </span>
                            , Confident, and{" "}
                            <span className="italic font-light text-white/95">
                                Respected
                            </span>{" "}
                            in <span className="text-[#D4AF37]">90 Days</span>.
                        </motion.h1>

                        <motion.p
                            data-testid="hero-subheadline"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.2 }}
                            className="mt-8 text-base sm:text-lg text-white/75 leading-relaxed max-w-2xl"
                        >
                            A 90-day transformation system for men ready to
                            rebuild their mindset, master attraction, and become
                            the type of man women naturally choose — without
                            games, scripts, or chasing.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.3 }}
                            className="mt-10 flex flex-col sm:flex-row gap-4 items-stretch sm:items-center"
                        >
                            <BookButton
                                testId="hero-book-cta"
                                label="Book a Free Consultation"
                                size="lg"
                            />
                            <a
                                href="/pricing"
                                data-testid="hero-enroll-cta"
                                className="inline-flex items-center justify-center gap-2 px-6 py-4 text-xs uppercase tracking-[0.18em] font-bold bg-transparent border border-[#1a2a4a] text-white hover:border-[#1E90FF] hover:text-[#00B7FF] hover:shadow-[0_0_30px_rgba(30,144,255,0.3)] transition-all"
                            >
                                Enroll Now — $2,999
                            </a>
                            <a
                                href="#problem"
                                data-testid="hero-scroll-link"
                                className="inline-flex items-center justify-center gap-2 px-2 py-4 text-xs uppercase tracking-[0.18em] font-semibold text-white/70 hover:text-[#00B7FF] transition-colors"
                            >
                                <ArrowDown size={16} /> Why Most Men Stay Stuck
                            </a>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.5 }}
                            className="mt-12 grid grid-cols-3 gap-px bg-[#142142] border border-[#142142] max-w-lg"
                        >
                            {[
                                { n: "90", l: "Days" },
                                { n: "4", l: "Core Modules" },
                                { n: "1:1", l: "Coaching" },
                            ].map((m) => (
                                <div
                                    key={m.l}
                                    className="bg-[#020617] px-4 py-5 text-center"
                                >
                                    <p className="font-display font-black text-[#00B7FF] text-3xl leading-none">
                                        {m.n}
                                    </p>
                                    <p className="mt-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-white/55">
                                        {m.l}
                                    </p>
                                </div>
                            ))}
                        </motion.div>
                    </div>

                    <div className="lg:col-span-5 relative">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.92 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            className="relative"
                        >
                            <div className="absolute inset-0 bg-[#1E90FF]/30 blur-3xl scale-110 animate-pulse-glow pointer-events-none" />
                            <img
                                src={PRODUCT_IMAGE}
                                alt="90 Day Alpha — The White Pill System product mockup"
                                className="relative w-full h-auto drop-shadow-[0_0_60px_rgba(30,144,255,0.45)]"
                                loading="eager"
                                data-testid="hero-product-image"
                            />
                        </motion.div>
                    </div>
                </div>
            </div>

            <div className="absolute bottom-0 inset-x-0 electric-divider" />
        </section>
    );
}
